import {
  AnnotationOperationError,
  type AnnotationOperation,
  type AnnotationOperationAvailability,
} from "@carbon6/c6-frontend-design/annotation";
import { isAxiosError } from "axios";
import { isHttpClientError, normalizeHttpError } from "@/http/httpError";
import type { AnnotationSetStatus } from "@/api";
import { gpuUnavailableForAnnotationReason } from "@/components/gpu";
import { storageBackendErrorText } from "@/components/dataset/storageBackendText";

const ABORTED = { en: "The operation was cancelled.", ko: "작업이 취소되었습니다." } as const;
const CONFLICT = {
  en: "The annotation set changed elsewhere. Reload it and try again.",
  ko: "다른 곳에서 Annotation Set이 변경되었습니다. 다시 불러온 뒤 재시도하세요.",
} as const;
const BUSY = {
  en: "The annotation service is busy. Try again shortly.",
  ko: "Annotation 서비스가 사용 중입니다. 잠시 후 재시도하세요.",
} as const;

function isRequestAbort(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (isAxiosError(error) && error.code === "ERR_CANCELED")
  );
}

export function toAnnotationOperationError(
  operation: AnnotationOperation,
  error: unknown,
): AnnotationOperationError {
  if (error instanceof AnnotationOperationError) return error;
  if (isRequestAbort(error)) {
    return new AnnotationOperationError(operation, "annotation.operation_aborted", {
      localizedUserMessage: ABORTED,
    });
  }

  const httpError = isHttpClientError(error) ? error : normalizeHttpError(error);
  const conflict = httpError.status === 409;
  const busy = httpError.status === 429 || httpError.code === "training.annotation_upstream_busy";
  // registry 가 상류(training-infra) 503 을 변환한 코드. Annotation 에서는 "GPU holder 가 없다" 가
  // 사실상 유일한 원인이다. 그래서 일반 서버 오류와 구분해 GPU 부재로 표시한다. 사전 게이트와 같은 문장이다.
  const upstreamUnavailable = httpError.code === "upstream.unavailable";
  const retryable =
    busy ||
    upstreamUnavailable ||
    httpError.kind === "network" ||
    httpError.kind === "timeout" ||
    httpError.kind === "server";
  // 저장 위치 오류도 409 다. "다른 곳에서 변경됨"과 다른 원인이라 먼저 가른다.
  const storageBackendText = storageBackendErrorText(httpError);
  const localizedUserMessage =
    storageBackendText !== null
      ? { en: httpError.message, ko: storageBackendText }
      : conflict
        ? CONFLICT
        : busy
          ? BUSY
          : upstreamUnavailable
            ? gpuUnavailableForAnnotationReason
            : undefined;

  return new AnnotationOperationError(operation, httpError.code ?? `http.${httpError.kind}`, {
    userMessage: localizedUserMessage === undefined ? httpError.message : undefined,
    localizedUserMessage,
    retryable,
    conflict,
  });
}

export function annotationAuthoringAvailability(
  status: AnnotationSetStatus | null,
): AnnotationOperationAvailability {
  if (status === null) {
    return {
      allowed: false,
      localizedReason: {
        en: "Select or create an annotation set before editing.",
        ko: "Annotation Set를 선택하거나 만든 뒤 작업을 시작해 주세요.",
      },
    };
  }
  return status === "committed"
    ? {
        allowed: false,
        localizedReason: {
          en: "Clone this committed set to a new draft before editing.",
          ko: "편집하려면 committed set을 새 draft로 복제하세요.",
        },
      }
    : { allowed: true };
}
